import { useMemo, useState } from 'react';
import { CalendarBlank, Clock, Plus, Spinner, Trash, UsersThree, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

import {
  useBusinessScheduleExceptions,
  useBusinessStaff,
  useCreateBusinessStaff,
  useDeleteBusinessScheduleException,
  useUpsertBusinessScheduleException,
  type BusinessScheduleExceptionType,
  type BusinessStaffStatus,
} from '@/features/staff';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { SideSheet } from '@/shared/ui/SideSheet';
import { StatusBadge, type StatusVariant } from '@/shared/ui/StatusBadge';
import { formatDate } from '@/shared/utils/formatters';

const STATUS_VARIANT: Record<BusinessStaffStatus, StatusVariant> = {
  active: 'success',
  on_leave: 'warning',
  suspended: 'danger',
  terminated: 'neutral',
};

const EXCEPTION_LABELS: Record<BusinessScheduleExceptionType, string> = {
  day_off: 'Kapalı gün',
  vacation: 'Yıllık izin',
  sick: 'Hastalık',
  holiday: 'Resmi tatil',
  reduced: 'Kısıtlı saat',
  extended: 'Uzatılmış saat',
  special_hours: 'Özel saat',
  custom: 'Özel durum',
};

export function StaffPage(): JSX.Element {
  const { data: staff, isLoading } = useBusinessStaff();
  const createStaff = useCreateBusinessStaff();
  const [actorId, setActorId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState<string | undefined>();
  const selectedStaff = staff?.find((member) => member.id === selectedStaffId);
  const exceptionsQuery = useBusinessScheduleExceptions(selectedStaffId);
  const upsertException = useUpsertBusinessScheduleException(selectedStaffId);
  const deleteException = useDeleteBusinessScheduleException(selectedStaffId);
  const [exceptionDate, setExceptionDate] = useState('');
  const [exceptionType, setExceptionType] = useState<BusinessScheduleExceptionType>('day_off');
  const [isAllDay, setIsAllDay] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const timeError = Boolean(!isAllDay && startTime && endTime && endTime <= startTime);

  const stats = useMemo(() => {
    const items = staff ?? [];
    return {
      total: items.length,
      active: items.filter((s) => s.status === 'active').length,
      leave: items.filter((s) => s.status === 'on_leave').length,
    };
  }, [staff]);

  const handleCreate = async () => {
    if (!actorId.trim() || !displayName.trim()) return;
    try {
      await createStaff.mutateAsync({
        actorId: actorId.trim(),
        displayName: displayName.trim(),
        role: 'groomer',
        employmentType: 'full_time',
        status: 'active',
      });
      setActorId('');
      setDisplayName('');
      toast.success('Ekip üyesi eklendi');
    } catch {
      toast.error('Ekip üyesi eklenemedi');
    }
  };

  const handleCreateException = async () => {
    if (!selectedStaffId || !exceptionDate || timeError) return;
    try {
      await upsertException.mutateAsync({
        exceptionDate,
        exceptionType,
        startTime: isAllDay ? null : startTime || null,
        endTime: isAllDay ? null : endTime || null,
        reason: reason.trim() || null,
      });
      setExceptionDate('');
      setIsAllDay(true);
      setStartTime('');
      setEndTime('');
      setReason('');
      toast.success('Takvim istisnası kaydedildi');
    } catch {
      toast.error('Takvim istisnası kaydedilemedi');
    }
  };

  const handleDeleteException = async (exceptionId: string) => {
    try {
      await deleteException.mutateAsync(exceptionId);
      toast.success('Takvim istisnası silindi');
    } catch {
      toast.error('Takvim istisnası silinemedi');
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1400px] mx-auto flex flex-col gap-biz-content-lg">
      <header>
        <h1 className="text-biz-2xl font-bold biz-heading">Ekip</h1>
        <p className="text-biz-sm text-biz-muted-foreground mt-1">
          Şube personeli, randevu atamaları ve çalışma planı temeli.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-biz-content-lg">
        <Metric label="Toplam ekip" value={stats.total} />
        <Metric label="Aktif" value={stats.active} />
        <Metric label="İzinli" value={stats.leave} />
      </section>

      <Card>
        <CardHeader>
          <div>
            <h2 className="font-semibold biz-heading">Yeni ekip üyesi</h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              Kimlik kaynağı platform `actors`; burada şube/rol bağlamı tutulur.
            </p>
          </div>
        </CardHeader>
        <CardBody className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-biz-content">
          <Input value={actorId} onChange={(e) => setActorId(e.target.value)} placeholder="Actor UUID" />
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Görünen ad" />
          <Button onClick={handleCreate} loading={createStaff.isPending} leftIcon={<Plus size={18} />}>
            Ekle
          </Button>
        </CardBody>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <h2 className="font-semibold biz-heading">Personel listesi</h2>
          <span className="text-biz-sm text-biz-muted-foreground">{isLoading ? 'Yükleniyor' : `${staff?.length ?? 0} kayıt`}</span>
        </CardHeader>
        <div className="divide-y divide-biz-border">
          {(staff ?? []).map((member) => (
            <div key={member.id} className="px-biz-card py-biz-content-lg flex items-center justify-between gap-biz-content-lg">
              <div className="flex items-center gap-biz-content-lg min-w-0">
                <div className="w-11 h-11 rounded-biz-full bg-biz-accent-subtle text-biz-accent flex items-center justify-center">
                  <UsersThree size={20} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{member.displayName}</p>
                  <p className="text-biz-sm text-biz-muted-foreground">
                    {member.role} · başlangıç {formatDate(member.startedAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-biz-content flex-wrap justify-end">
                <StatusBadge variant={STATUS_VARIANT[member.status]} label={member.status} />
                <Button
                  type="button"
                  intent="secondary"
                  size="sm"
                  leftIcon={<CalendarBlank size={16} />}
                  onClick={() => setSelectedStaffId(member.id)}
                >
                  İstisnalar
                </Button>
              </div>
            </div>
          ))}
          {!isLoading && (staff?.length ?? 0) === 0 ? (
            <div className="px-biz-card py-biz-card text-biz-sm text-biz-muted-foreground">
              Henüz ekip kaydı yok. Actor bağlamı ile ilk personeli ekleyin.
            </div>
          ) : null}
        </div>
      </Card>

      <SideSheet
        open={Boolean(selectedStaffId)}
        onOpenChange={(open) => {
          if (!open) setSelectedStaffId(undefined);
        }}
        title="Takvim istisnaları"
        description={
          selectedStaff
            ? `${selectedStaff.displayName} için izin, hastalık, kapalı gün ve kısıtlı saat kayıtları.`
            : undefined
        }
        closeLabel="Kapat"
        footer={
          <>
            <Button type="button" intent="secondary" onClick={() => setSelectedStaffId(undefined)}>
              Kapat
            </Button>
            <Button
              type="button"
              onClick={handleCreateException}
              loading={upsertException.isPending}
              disabled={!exceptionDate || timeError}
            >
              Kaydet
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-biz-content-lg">
          <Card>
            <CardHeader>
              <div>
                <h3 className="font-semibold biz-heading">Yeni istisna</h3>
                <p className="text-biz-sm text-biz-muted-foreground">
                  Kaydedilen kapalı veya kısıtlı saatler randevu optimizer tarafından dikkate alınır.
                </p>
              </div>
            </CardHeader>
            <CardBody className="grid grid-cols-1 sm:grid-cols-2 gap-biz-content">
              <Input
                type="date"
                value={exceptionDate}
                onChange={(event) => setExceptionDate(event.target.value)}
                aria-label="İstisna tarihi"
              />
              <Select
                value={exceptionType}
                onChange={(event) => setExceptionType(event.target.value as BusinessScheduleExceptionType)}
                aria-label="İstisna türü"
              >
                {Object.entries(EXCEPTION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <label className="sm:col-span-2 inline-flex items-center gap-biz-content text-biz-sm text-biz-foreground">
                <input
                  type="checkbox"
                  checked={isAllDay}
                  onChange={(event) => {
                    setIsAllDay(event.target.checked);
                    if (event.target.checked) {
                      setStartTime('');
                      setEndTime('');
                    }
                  }}
                  className="h-4 w-4 rounded border-biz-border accent-biz-primary"
                />
                Tüm gün
              </label>
              {!isAllDay ? (
                <>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(event) => setStartTime(event.target.value)}
                    placeholder="Başlangıç"
                    aria-label="Başlangıç saati"
                  />
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(event) => setEndTime(event.target.value)}
                    placeholder="Bitiş"
                    aria-label="Bitiş saati"
                    aria-invalid={timeError || undefined}
                  />
                  {timeError ? (
                    <div className="sm:col-span-2 flex items-center gap-biz-content text-biz-sm text-biz-danger">
                      <WarningCircle size={16} weight="fill" aria-hidden="true" />
                      Bitiş saati başlangıçtan sonra olmalı.
                    </div>
                  ) : null}
                </>
              ) : null}
              <label className="sm:col-span-2 flex flex-col gap-1.5">
                <span className="text-biz-xs font-semibold text-biz-muted-foreground">Sebep / not</span>
                <textarea
                  className="min-h-[88px] w-full rounded-biz-input border border-biz-input bg-biz-background px-biz-content-lg py-biz-content text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Örn. yıllık izin, hastalık raporu, eğitim..."
                  aria-label="İstisna sebebi"
                />
              </label>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="font-semibold biz-heading">Kayıtlı istisnalar</h3>
              <span className="text-biz-sm text-biz-muted-foreground">
                {exceptionsQuery.isLoading
                  ? 'Yükleniyor'
                  : `${exceptionsQuery.data?.length ?? 0} kayıt`}
              </span>
            </CardHeader>
            <div className="divide-y divide-biz-border">
              {exceptionsQuery.isLoading ? (
                <ExceptionLoadingRows />
              ) : exceptionsQuery.isError ? (
                <ExceptionErrorState onRetry={() => exceptionsQuery.refetch()} />
              ) : (
                [...(exceptionsQuery.data ?? [])]
                  .sort((a, b) => a.exceptionDate.localeCompare(b.exceptionDate))
                  .map((item) => (
                <div
                  key={item.id}
                  className="px-biz-card py-biz-content flex items-start justify-between gap-biz-content"
                >
                  <div className="flex items-start gap-biz-content min-w-0">
                    <div className="min-h-biz-touch min-w-biz-touch rounded-biz-btn bg-biz-muted text-biz-muted-foreground flex items-center justify-center">
                      <CalendarBlank size={18} aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-biz-content flex-wrap">
                        <p className="font-medium biz-heading">{formatDate(item.exceptionDate)}</p>
                        <StatusBadge variant="warning" label={EXCEPTION_LABELS[item.exceptionType]} />
                      </div>
                      <p className="text-biz-sm text-biz-muted-foreground flex items-center gap-1.5 mt-1">
                        <Clock size={13} aria-hidden="true" />
                        {item.startTime && item.endTime
                          ? `${item.startTime.slice(0, 5)}-${item.endTime.slice(0, 5)}`
                          : 'Tüm gün'}
                      </p>
                      {item.reason ? (
                        <p className="text-biz-sm text-biz-muted-foreground mt-1 truncate">
                          {item.reason}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    type="button"
                    intent="ghost"
                    size="icon"
                    aria-label="İstisnayı sil"
                    onClick={() => handleDeleteException(item.id)}
                    loading={deleteException.isPending}
                  >
                    <Trash size={18} aria-hidden="true" />
                  </Button>
                </div>
                  ))
              )}
              {!exceptionsQuery.isLoading && !exceptionsQuery.isError && (exceptionsQuery.data?.length ?? 0) === 0 ? (
                <ExceptionEmptyState />
              ) : null}
            </div>
          </Card>
        </div>
      </SideSheet>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <Card>
      <CardBody>
        <p className="text-biz-sm text-biz-muted-foreground">{label}</p>
        <p className="text-biz-2xl font-bold biz-heading mt-1">{value}</p>
      </CardBody>
    </Card>
  );
}

function ExceptionLoadingRows(): JSX.Element {
  return (
    <div className="px-biz-card py-biz-content space-y-biz-content" aria-busy="true">
      <div className="flex items-center gap-biz-content text-biz-sm text-biz-muted-foreground">
        <Spinner size={14} className="animate-spin" aria-hidden="true" />
        İstisnalar yükleniyor...
      </div>
      {[0, 1, 2].map((item) => (
        <div key={item} className="flex items-start gap-biz-content">
          <div className="h-10 w-10 rounded-biz-btn bg-biz-muted animate-pulse" />
          <div className="flex-1 space-y-2 py-1">
            <div className="h-3 w-1/2 rounded-biz-full bg-biz-muted animate-pulse" />
            <div className="h-3 w-1/3 rounded-biz-full bg-biz-muted animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ExceptionEmptyState(): JSX.Element {
  return (
    <div className="px-biz-card py-biz-card text-center">
      <div className="mx-auto h-12 w-12 rounded-biz-full bg-biz-primary/10 text-biz-primary flex items-center justify-center mb-biz-content">
        <CalendarBlank size={22} aria-hidden="true" />
      </div>
      <p className="font-semibold biz-heading">Henüz istisna yok</p>
      <p className="text-biz-sm text-biz-muted-foreground mt-1 max-w-sm mx-auto">
        Bu personel için planlı izin veya müsaitlik dışı saat tanımlanmamış.
      </p>
    </div>
  );
}

function ExceptionErrorState({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div className="px-biz-card py-biz-card text-center">
      <div className="mx-auto h-12 w-12 rounded-biz-full bg-biz-danger/10 text-biz-danger flex items-center justify-center mb-biz-content">
        <WarningCircle size={22} weight="fill" aria-hidden="true" />
      </div>
      <p className="font-semibold biz-heading">İstisnalar yüklenemedi</p>
      <p className="text-biz-sm text-biz-muted-foreground mt-1">
        Sunucuya bağlanırken bir sorun oluştu.
      </p>
      <Button type="button" intent="secondary" size="sm" className="mt-biz-content" onClick={onRetry}>
        Yeniden dene
      </Button>
    </div>
  );
}
